import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { AtomIcon, AwardIcon, MicroscopeIcon, WavesIcon } from '@/components/icons';
import { Button } from '@/components/ui';

interface Bullet {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  labelKey: string;
}

const BULLETS: readonly Bullet[] = [
  { Icon: AwardIcon, labelKey: 'home.hero.bullets.award' },
  { Icon: MicroscopeIcon, labelKey: 'home.hero.bullets.sihy' },
  { Icon: AtomIcon, labelKey: 'home.hero.bullets.hioxifilcon' },
  { Icon: WavesIcon, labelKey: 'home.hero.bullets.aquagrip' },
] as const;

export function HeroSection() {
  const { t } = useTranslation();
  return (
    <section
      aria-label={t('home.hero.ariaLabel')}
      className="relative mb-20 h-[800px] w-full overflow-hidden bg-clearlab-oat"
    >
      <div aria-hidden className="absolute inset-0">
        {/* Responsive hero (LCP element). WebP srcset keeps mobile payload tiny
            (~26KB at 640w vs ~715KB full-size); JPG is the fallback. Preloaded
            in index.html so the fetch starts before React hydrates. */}
        <picture>
          <source
            type="image/webp"
            srcSet="/assets/hero-bg-640.webp 640w, /assets/hero-bg-1024.webp 1024w, /assets/hero-bg-1536.webp 1536w, /assets/hero-bg-2048.webp 2048w"
            sizes="100vw"
          />
          <img
            src="/assets/hero-bg-fallback.jpg"
            alt=""
            width={960}
            height={1280}
            loading="eager"
            fetchPriority="high"
            className="h-full w-full object-cover"
          />
        </picture>
        <div className="absolute inset-0 bg-[rgba(168,179,206,0.2)] mix-blend-color" />
        {/* Figma-matched fade shape: transparent above 68% of the section,
            ramping to solid by 87%. End stop is clearlab-oat at 100%
            opacity (not figma's 90% white) so the bottom is pure #FAFAFA
            and bleeds seamlessly into the next section. */}
        <div className="absolute inset-0 bg-gradient-to-b from-clearlab-oat/0 from-[68%] to-clearlab-oat to-[87%]" />
      </div>

      <div className="relative flex h-full items-end px-6 py-14 md:p-14 lg:p-20 xl:px-30 xl:py-20">
        <div className="mx-auto flex w-full max-w-content-xl flex-col items-start gap-10 xl:flex-row xl:items-end xl:justify-between xl:gap-10">
          <h1 className="whitespace-nowrap text-heading-24 font-semibold tracking-tight text-clearlab-midnight">
            {t('home.hero.wordmark')}
          </h1>

          {/* Bullets + button: stacked at md/below, row at lg, dissolve into
              the outer flex-row at xl via `display: contents`. */}
          <div className="flex w-full flex-col gap-10 lg:flex-row lg:items-end lg:justify-between xl:contents">
            <div className="flex w-full flex-col gap-y-4 md:flex-row md:items-start md:justify-start md:gap-x-10 md:gap-y-0 lg:w-auto xl:gap-x-20">
              {[BULLETS.slice(0, 2), BULLETS.slice(2, 4)].map((column, colIndex) => (
                <ul key={colIndex} className="flex flex-col gap-y-4">
                  {column.map(({ Icon, labelKey }) => (
                    <li key={labelKey} className="flex items-center gap-4">
                      <Icon className="size-4 shrink-0 text-clearlab-steel" />
                      <p className="whitespace-nowrap text-label-16 text-clearlab-steel">
                        {t(labelKey)}
                      </p>
                    </li>
                  ))}
                </ul>
              ))}
            </div>

            <Button
              to="/products"
              variant="transparent"
              size="small"
              tone="blue"
              iconRight
              className="whitespace-nowrap lg:self-end xl:self-end"
            >
              {t('home.hero.cta')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
